import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useEffect, useCallback, useRef } from "react";
import { TextField, IconButton } from "@fluentui/react";
import { Sparkle20Filled } from "@fluentui/react-icons";
import { recommendationInputStyles } from "./RecommendationInput.styles";
import { useDebounce } from "./useDebounce";
import { useLocaleStrings } from "../../../library/providers/strings";
import { logEvent } from "../../../library/logging/loggingService";
const MIN_QUERY_LENGTH = 10;
const MAX_QUERY_LENGTH = 500;
/** Maps status hints to localised string keys and style variants. */
const STATUS_HINT_CONFIG = {
    no_result: { stringKey: "cardRecommendationNoResult", isWarning: false },
    throttled: { stringKey: "cardRecommendationThrottled", isWarning: true },
    error: { stringKey: "cardRecommendationError", isWarning: true },
};
const DISCLAIMER_ID = "recommendation-ai-disclaimer";
const RecommendationInput = (props) => {
    const { onQueryReady, isLoading, recommendedCardId, statusHint } = props;
    const { getLocalSpecificString } = useLocaleStrings();
    const styles = recommendationInputStyles;
    const [inputValue, setInputValue] = useState("");
    const debouncedValue = useDebounce(inputValue, 500);
    // Stabilise callback ref to prevent infinite re-render loops when the
    // parent recreates onQueryReady on every render (e.g. unstable useAgent ref).
    const onQueryReadyRef = useRef(onQueryReady);
    useEffect(() => {
        onQueryReadyRef.current = onQueryReady;
    }, [onQueryReady]);
    // Track whether a query has been submitted so we only send clear signals
    // after a prior submission (avoids redundant clears during initial typing).
    const hasSubmittedRef = useRef(false);
    useEffect(() => {
        const trimmed = debouncedValue.trim();
        if (trimmed.length >= MIN_QUERY_LENGTH) {
            logEvent("CardRecommendation.InputSubmitted", {
                component: "RecommendationInput",
                action: "input_submitted",
                inputLength: trimmed.length,
            });
            onQueryReadyRef.current(trimmed);
            hasSubmittedRef.current = true;
        }
        else if (hasSubmittedRef.current) {
            // Input dropped below threshold after a prior submission — clear stale recommendation
            onQueryReadyRef.current("");
            hasSubmittedRef.current = false;
        }
    }, [debouncedValue]);
    const handleInputChange = useCallback((_event, newValue) => {
        setInputValue(newValue !== null && newValue !== void 0 ? newValue : "");
    }, []);
    const handleClear = useCallback(() => {
        setInputValue("");
        onQueryReadyRef.current("");
        logEvent("CardRecommendation.Dismissed", {
            component: "RecommendationInput",
            action: "dismissed",
            previousRecommendedCardId: recommendedCardId !== null && recommendedCardId !== void 0 ? recommendedCardId : undefined,
        });
    }, [recommendedCardId]);
    return (_jsxs("div", Object.assign({ className: styles.wrapper, "data-testid": "recommendation-input-wrapper" }, { children: [_jsxs("div", Object.assign({ className: styles.inputRow }, { children: [_jsx(Sparkle20Filled, { className: styles.icon, "aria-hidden": "true" }), _jsx(TextField, { className: styles.input, placeholder: getLocalSpecificString("cardRecommendationPlaceholder"), ariaLabel: getLocalSpecificString("cardRecommendationAriaLabel"), "aria-describedby": DISCLAIMER_ID, value: inputValue, onChange: handleInputChange, maxLength: MAX_QUERY_LENGTH, borderless: true, "data-testid": "recommendation-input" }), inputValue && (_jsx(IconButton, { iconProps: { iconName: "Cancel" }, onClick: handleClear, ariaLabel: getLocalSpecificString("cardRecommendationClearAriaLabel"), "data-testid": "recommendation-clear-button", className: styles.clearButton }))] })), _jsx("div", Object.assign({ id: DISCLAIMER_ID, className: styles.disclaimer, "data-testid": "recommendation-disclaimer" }, { children: getLocalSpecificString("cardRecommendationDisclaimer") })), isLoading && (_jsx("div", { className: styles.shimmer, role: "progressbar", "aria-label": getLocalSpecificString("cardRecommendationLoadingAriaLabel"), "data-testid": "recommendation-shimmer" })), !isLoading && statusHint && (_jsx("div", Object.assign({ className: STATUS_HINT_CONFIG[statusHint].isWarning ? styles.statusWarning : styles.statusText, role: "status", "aria-live": "polite", "data-testid": "recommendation-status" }, { children: getLocalSpecificString(STATUS_HINT_CONFIG[statusHint].stringKey) })))] })));
};
export default RecommendationInput;
